import React, {useMemo, useState} from 'react'
import styled from "styled-components";
import {Button, Calendar, Popover} from "antd";
import dayjs, {Dayjs} from "dayjs";
import {useQuery} from "react-query";
import {REPO} from "../../Repository/repo";
import {VACATION_CALENDAR_CHIP} from "../../Constant/Vacation";
import {VacationCalendarItem} from "../../Model/VacationModel";
import {queryKeys} from "../../Constant/QueryKeys";
import useUser from "../../Hooks/useUser";
import useHolidays from "../../Hooks/useHolidays";

const Wrap = styled.div`
  .ant-picker-calendar-mini {
    border: 1px solid #f0f0f0;
    border-radius: 4px;
  }
  .vac-mini-cell {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .holiday-date {
    color: #f03e3e;
  }
  .holiday-label {
    color: #f03e3e;
    font-size: 10px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vac-mini-chip {
    color: white;
    font-size: 10px;
    border-radius: 3px;
    padding: 0 4px;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .vac-mini-more {
    font-size: 10px;
    color: #1677ff;
    cursor: pointer;
  }
`

const MAX_CHIPS_PER_CELL_MINI = 2

const labelOf = (it: VacationCalendarItem) => {
    const fraction = parseFloat(String((it as any).vacation_unit_fraction ?? 1))
    if (fraction === 0.5 && it.vacation_period_label === "오전") return "반차(오전)"
    if (fraction === 0.5 && it.vacation_period_label === "오후") return "반차(오후)"
    return "휴가"
}
const colorOf = (label: string) => label.startsWith("반차") ? VACATION_CALENDAR_CHIP.반차 : VACATION_CALENDAR_CHIP.휴가

const VacationMini: React.FC = () => {
    const {userData} = useUser()
    const {isHoliday, holidayName} = useHolidays()
    const isHrAdmin = !!userData?.data?.is_hr_admin
    const [displayDate, setDisplayDate] = useState<Dayjs>(dayjs())

    const year = displayDate.year()
    const month = displayDate.month() + 1

    const {data} = useQuery(
        [...queryKeys.vacationCalendar, year, month],
        async () => {
            const res = await REPO.Vacation.GetCalendar({year, month})
            return res.data
        }
    )

    const goToDocument = (it: VacationCalendarItem) => {
        window.location.href = `/Vacation/Approval/${it.vacation_approval_seq}`
    }

    const itemsByDate = useMemo(() => {
        const map: Record<string, VacationCalendarItem[]> = {}
        const list = data?.list || []
        list.forEach(it => {
            let cur = dayjs(it.vacation_date_start)
            const end = dayjs(it.vacation_date_end)
            while (cur.isBefore(end) || cur.isSame(end, 'day')) {
                const key = cur.format("YYYY-MM-DD")
                if (!map[key]) map[key] = []
                map[key].push(it)
                cur = cur.add(1, 'day')
            }
        })
        return map
    }, [data])

    const cellRender = (current: Dayjs) => {
        if (current.month() !== displayDate.month()) return null
        const key = current.format("YYYY-MM-DD")
        const items = itemsByDate[key] || []
        if (!items.length) return null

        const visible = items.slice(0, MAX_CHIPS_PER_CELL_MINI)
        const overflow = items.length - visible.length

        return (
            <div className={"vac-mini-cell"}>
                {visible.map((it, idx) => {
                    const lb = labelOf(it)
                    const text = `${it.employee_name} · ${lb}`
                    const chip = (
                        <div
                            key={idx}
                            className={"vac-mini-chip"}
                            style={{background: colorOf(lb), cursor: isHrAdmin ? "pointer" : "default"}}
                            title={text}
                            onClick={isHrAdmin ? (e) => e.stopPropagation() : undefined}
                        >
                            {text}
                        </div>
                    )
                    return isHrAdmin ? (
                        <Popover
                            key={idx}
                            content={
                                <div style={{padding: "4px 0"}}>
                                    <div style={{marginBottom: 8}}>{text}</div>
                                    <Button size={"small"} type={"primary"} onClick={() => goToDocument(it)}>문서로 이동</Button>
                                </div>
                            }
                            trigger={"click"}
                        >
                            {chip}
                        </Popover>
                    ) : chip
                })}
                {overflow > 0 && (
                    <Popover
                        content={
                            <div style={{maxWidth: 280}}>
                                {items.map((it, idx) => {
                                    const lb = labelOf(it)
                                    return (
                                        <div key={idx} style={{padding: "4px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}>
                                            <span>
                                                <span style={{
                                                    display: "inline-block",
                                                    width: 8, height: 8, borderRadius: 4,
                                                    background: colorOf(lb), marginRight: 6
                                                }}/>
                                                {it.employee_name} · {lb}
                                            </span>
                                            {isHrAdmin && (
                                                <Button size={"small"} onClick={() => goToDocument(it)}>이동</Button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        }
                        title={current.format("YYYY-MM-DD")}
                        trigger={"click"}
                    >
                        <span className={"vac-mini-more"}>+{overflow}건</span>
                    </Popover>
                )}
            </div>
        )
    }

    // antd 기본 셀 마크업을 복제하면서 토/일/공휴일 날짜를 빨강 + 공휴일명 표시
    const fullCellRender = (current: Dayjs) => {
        const inView = current.month() === displayDate.month()
        const isToday = dayjs().isSame(current, 'day')
        const name = inView ? holidayName(current) : undefined
        const isRed = inView && (current.day() === 0 || current.day() === 6 || isHoliday(current))
        return (
            <div className={`ant-picker-cell-inner ant-picker-calendar-date${isToday ? " ant-picker-calendar-date-today" : ""}`}>
                <div className={`ant-picker-calendar-date-value${isRed ? " holiday-date" : ""}`} title={name}>
                    {String(current.date()).padStart(2, "0")}
                </div>
                <div className={"ant-picker-calendar-date-content"}>
                    {name && <div className={"holiday-label"} title={name}>{name}</div>}
                    {cellRender(current)}
                </div>
            </div>
        )
    }

    return (
        <Wrap>
            <Calendar
                fullscreen={false}
                value={displayDate}
                onPanelChange={(d) => setDisplayDate(d)}
                onChange={(d) => setDisplayDate(d)}
                dateFullCellRender={fullCellRender}
            />
        </Wrap>
    )
}

export default VacationMini
